import { useEffect, useMemo, useRef, useState } from "react"
import {
  faChevronDown,
  faMagnifyingGlass,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Link as LinkIcon, Prohibit, ArrowUUpLeft } from "@phosphor-icons/react"
import Loader from "../../../components/Loader"
import {
  useMissingPathologyTermsInfinite,
  useMissingPathologyLogsByTerm,
  useMissingPathologyLogByLogId,
  useUpdatePathologyTermMapping,
} from "../../../hooks/useMissingPathologyLogs"
import MapPathologyModal from "./MapPathologyModal"

const STATUS_TABS = [
  { key: "UNMAPPED", label: "Unmapped" },
  { key: "MAPPED", label: "Mapped" },
  { key: "IGNORED", label: "Ignored" },
]

const formatSortBy = (sorting) =>
  sorting
    .map((s) => `${s.id}:${s.desc ? "DESC" : "ASC"}`)
    .filter(Boolean)
    .join(",") || "UpdatedAt:DESC"

const MissingPathologyStatsTable = ({
  bodyPart,
  bodyPartOptions,
  counts,
  dateFrom,
  dateTo,
  search,
  setBodyPart,
  setSearch,
  setSorting,
  setStatus,
  sorting,
  status,
}) => {
  const [selectedTermId, setSelectedTermId] = useState(null)
  const [selectedTerm, setSelectedTerm] = useState(null)
  const [selectedMissingPathologyLogId, setSelectedMissingPathologyLogId] =
    useState(null)
  const [mapModalTerm, setMapModalTerm] = useState(null)
  const [searchInput, setSearchInput] = useState(search)

  const sentinelRef = useRef(null)

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    if (searchInput === search) return
    const handle = setTimeout(() => {
      setSearch(searchInput)
    }, 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  const sortBy = formatSortBy(sorting)
  const filters = useMemo(
    () => ({
      status,
      BodyPart: bodyPart || undefined,
      search: search || undefined,
    }),
    [status, bodyPart, search]
  )

  const {
    data: termPages,
    isLoading: isLoadingTerms,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useMissingPathologyTermsInfinite({
    limit: 10,
    sortBy,
    filters,
  })

  const rows = useMemo(
    () => (termPages?.pages || []).flatMap((p) => p?.data || []),
    [termPages]
  )

  useEffect(() => {
    setSelectedTermId(null)
    setSelectedTerm(null)
    setSelectedMissingPathologyLogId(null)
  }, [status, bodyPart, search])

  // Auto-select first row of the first page.
  useEffect(() => {
    if (!selectedTermId && rows.length > 0) {
      const first = rows[0]
      setSelectedTermId(first.PathologyTermMappingId)
      setSelectedTerm(first)
    }
  }, [rows, selectedTermId])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: node.parentElement, rootMargin: "0px 0px 100px 0px" }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length])

  const byTermFilters = useMemo(
    () => ({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [dateFrom, dateTo]
  )

  const { data: logsByTermResp, isLoading: isLoadingLogsByTerm } =
    useMissingPathologyLogsByTerm({
      pathologyTermMappingId: selectedTermId,
      page: 1,
      limit: 20,
      sortBy: "createdAt:DESC",
      filters: byTermFilters,
    })

  const accessionRows = useMemo(
    () => logsByTermResp?.data || [],
    [logsByTermResp]
  )

  const { data: missingPathologyLog, isLoading: isLoadingMissingPathologyLog } =
    useMissingPathologyLogByLogId(selectedMissingPathologyLogId)

  useEffect(() => {
    setSelectedMissingPathologyLogId(null)
  }, [selectedTermId])

  useEffect(() => {
    if (
      !isLoadingLogsByTerm &&
      accessionRows.length > 0 &&
      !selectedMissingPathologyLogId
    ) {
      setSelectedMissingPathologyLogId(accessionRows[0].MissingPathologiesLogId)
    }
  }, [isLoadingLogsByTerm, accessionRows, selectedMissingPathologyLogId])

  const toggleSort = (field) => {
    setSorting((prev) => {
      const current = prev.find((s) => s.id === field)
      if (!current) return [{ id: field, desc: true }]
      if (current.desc) return [{ id: field, desc: false }]
      return []
    })
  }
  const sortIcon = (field) => {
    const current = sorting.find((s) => s.id === field)
    if (!current) return faSort
    return current.desc ? faSortDown : faSortUp
  }

  const updateMutation = useUpdatePathologyTermMapping()
  const isRowMutating = (termId) =>
    updateMutation.isPending &&
    updateMutation.variables?.pathologyTermMappingId === termId

  const handleIgnore = (term) => {
    updateMutation.mutate({
      pathologyTermMappingId: term.PathologyTermMappingId,
      Status: "IGNORED",
    })
  }
  const handleMoveBackToUnmapped = (term) => {
    updateMutation.mutate({
      pathologyTermMappingId: term.PathologyTermMappingId,
      Status: "UNMAPPED",
    })
  }
  const handleMap = (term) => {
    setMapModalTerm(term)
  }

  const renderActions = (term) => {
    const disabled = isRowMutating(term.PathologyTermMappingId)
    const baseBtn =
      "inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-xs font-medium border shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
    const stop = (e) => e.stopPropagation()

    if (term.Status === "IGNORED") {
      return (
        <div className="flex items-center justify-start gap-1.5">
          <button
            className={`${baseBtn} bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 hover:border-amber-400`}
            disabled={disabled}
            title="Move back to unmapped"
            onClick={(e) => {
              stop(e)
              handleMoveBackToUnmapped(term)
            }}
          >
            <ArrowUUpLeft size={14} weight="bold" />
            <span>Restore</span>
          </button>
        </div>
      )
    }

    return (
      <div className="flex items-center justify-end gap-1.5">
        <button
          className={`${baseBtn} bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:border-blue-700`}
          disabled={disabled}
          title="Map to canonical"
          onClick={(e) => {
            stop(e)
            handleMap(term)
          }}
        >
          <LinkIcon size={14} weight="bold" />
          <span>Map</span>
        </button>
        <button
          className={`${baseBtn} bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400`}
          disabled={disabled}
          title="Ignore"
          onClick={(e) => {
            stop(e)
            handleIgnore(term)
          }}
        >
          <Prohibit size={14} weight="bold" />
          <span>Ignore</span>
        </button>
      </div>
    )
  }

  return (
    <>
      <div className="flex gap-4 h-[75vh] p-4">
        {/* Left Panel - Tabs + Filters + Pathologies Table */}
        <div className="w-1/3 flex flex-col">
          {/* Status tabs */}
          <div className="flex border-b border-gray-200 mb-3">
            {STATUS_TABS.map((tab) => {
              const isActive = status === tab.key
              const count = counts?.[tab.key] ?? 0
              return (
                <button
                  key={tab.key}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  type="button"
                  onClick={() => setStatus(tab.key)}
                >
                  {tab.label}
                  <span
                    className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isActive
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          {/* BodyPart (1/4) + search (3/4) filters */}
          <div className="flex gap-2 mb-3">
            <div className="relative w-1/4">
              <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">
                Body Part
              </label>
              <div className="relative">
                <select
                  className="appearance-none rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                  value={bodyPart}
                  onChange={(e) => setBodyPart(e.target.value)}
                >
                  {(bodyPartOptions || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute top-[50%] right-0 pr-2 translate-y-[-50%]">
                  <FontAwesomeIcon
                    aria-hidden="true"
                    className="h-4 w-4 text-gray-400"
                    icon={faChevronDown}
                  />
                </div>
              </div>
            </div>
            <div className="w-3/4">
              <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">
                Search Pathology
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400 h-3.5 w-3.5"
                  icon={faMagnifyingGlass}
                />
                <input
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search..."
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Pathology table */}
          <div className="flex-1 overflow-hidden">
            <div className="bg-white border border-gray-200 rounded-lg h-full flex flex-col">
              <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs font-semibold text-gray-700 uppercase">
                <button
                  className={`${
                    status === "MAPPED" ? "col-span-6" : "col-span-5"
                  } text-left flex items-center gap-1 hover:text-gray-900`}
                  type="button"
                  onClick={() => toggleSort("Pathology")}
                >
                  Pathology
                  <FontAwesomeIcon
                    className="h-3 w-3 text-gray-400"
                    icon={sortIcon("Pathology")}
                  />
                </button>
                {status === "MAPPED" && (
                  <div className="col-span-3 text-left">Canonical</div>
                )}
                <button
                  className="col-span-3 text-left flex items-center justify-start gap-1 hover:text-gray-900"
                  type="button"
                  onClick={() => toggleSort("logCount")}
                >
                  Count
                  <FontAwesomeIcon
                    className="h-3 w-3 text-gray-400"
                    icon={sortIcon("logCount")}
                  />
                </button>
                {status !== "MAPPED" && (
                  <div className="col-span-4 text-left">Actions</div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {isLoadingTerms ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader className="border-gray-500" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    No pathology terms found.
                  </div>
                ) : (
                  <div>
                    {rows.map((row) => {
                      const isSelected =
                        selectedTermId === row.PathologyTermMappingId
                      return (
                        <div
                          key={row.PathologyTermMappingId}
                          className={`grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                            isSelected
                              ? "bg-blue-50 border-l-4 border-l-blue-500"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedTermId(row.PathologyTermMappingId)
                            setSelectedTerm(row)
                          }}
                        >
                          <div
                            className={`${
                              status === "MAPPED" ? "col-span-6" : "col-span-5"
                            } text-sm text-gray-700 truncate`}
                          >
                            {row.Pathology || ""}
                          </div>
                          {status === "MAPPED" && (
                            <div className="col-span-3 text-xs text-gray-500 truncate">
                              {row.Canonical?.CanonicalName ?? "—"}
                            </div>
                          )}
                          <div className="col-span-3 text-sm font-semibold text-gray-900 text-left">
                            {Number(row.logCount) || 0}
                          </div>
                          {status !== "MAPPED" && (
                            <div className="col-span-4 flex justify-start">
                              {renderActions(row)}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    <div ref={sentinelRef} className="py-3 text-center">
                      {isFetchingNextPage ? (
                        <Loader className="border-gray-500" />
                      ) : !hasNextPage ? (
                        <span className="text-xs text-gray-400">
                          No more results
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Panel - Recent Reports */}
        {selectedTerm && (
          <div className="w-1/6 flex flex-col">
            <div className="mb-4">
              <h3 className="text-sm font-bold text-gray-600 uppercase">
                Recent Reports
              </h3>
              <p className="text-xs text-gray-400">
                <span className="italic font-semibold">
                  {selectedTerm.Pathology}
                </span>{" "}
                cases
              </p>
            </div>
            <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="h-full overflow-y-auto p-2">
                {isLoadingLogsByTerm ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader className="border-gray-500" />
                  </div>
                ) : accessionRows.length > 0 ? (
                  <div className="space-y-2">
                    {accessionRows.map((log) => (
                      <div
                        key={log.MissingPathologiesLogId}
                        className={`p-2 rounded cursor-pointer text-sm transition-colors ${
                          selectedMissingPathologyLogId ===
                          log.MissingPathologiesLogId
                            ? "bg-blue-100 text-blue-800 border border-blue-300"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() =>
                          setSelectedMissingPathologyLogId(
                            log.MissingPathologiesLogId
                          )
                        }
                      >
                        {log.AccessionNumber}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No reports found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Right Panel - Report Details */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col gap-4">
            <div className="flex-1 bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                <h4 className="text-sm font-bold text-gray-600 flex items-center">
                  Report Text
                </h4>
              </div>
              <div className="h-full overflow-y-auto p-4">
                <div className="text-sm text-gray-700 leading-relaxed">
                  {selectedMissingPathologyLogId ? (
                    isLoadingMissingPathologyLog ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader className="border-gray-500" />
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                        {missingPathologyLog?.log?.ExtractedReportText ||
                          "No report text available"}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Select a report to view details
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                <h4 className="text-sm font-bold text-gray-600 flex items-center">
                  AI Response
                </h4>
              </div>
              <div className="h-full p-4 max-h-[250px] overflow-y-auto">
                <div className="text-sm text-gray-700">
                  {selectedMissingPathologyLogId ? (
                    isLoadingMissingPathologyLog ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader className="border-gray-500" />
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap font-mono text-xs">
                        {(() => {
                          try {
                            const response =
                              missingPathologyLog?.log?.AIResponse
                            if (!response) return "No AI response available"
                            const parsed = JSON.parse(response)
                            return JSON.stringify(parsed, null, 2)
                          } catch (error) {
                            return (
                              missingPathologyLog?.log?.AIResponse ||
                              "No AI response available"
                            )
                          }
                        })()}
                      </pre>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Select a report to view AI response
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MapPathologyModal
        isOpen={!!mapModalTerm}
        term={mapModalTerm}
        onClose={() => setMapModalTerm(null)}
      />
    </>
  )
}

export default MissingPathologyStatsTable
